import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBinoculars,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { IconWithHelperText } from '../../../components/TableRow'
import { diffCardTitle } from './ChangeRequestDiffCards'
import FindingsBadge from './FindingsBadge'
import { findingsForOperation, reviewBlockLink } from './blockChangeHelpers'

const COLUMN_COUNT = 7
const FIELD_LABELS = { block_action: 'action', customer_type: 'customer' }

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

function formatValue(field, value, { actionLabels, treatmentNamesById }) {
  if (value === null || value === undefined || value === '') return '—'
  if (field === 'block_action') return actionLabels[value] ?? String(value)
  if (field !== 'treatment_id') return String(value)
  return treatmentNamesById[value] ?? String(value)
}

const conditionText = (condition) =>
  `${condition.field_name} = "${condition.field_value}"`

// One line per field change, then one per condition added or removed.
function changeLines(card, labels) {
  return [
    ...card.field_changes.map((change) => ({
      key: `field-${change.field}`,
      field: FIELD_LABELS[change.field] ?? change.field,
      before: formatValue(change.field, change.before, labels),
      after: formatValue(change.field, change.after, labels),
    })),
    ...card.conditions_removed.map((condition, index) => ({
      key: `removed-${index}-${conditionText(condition)}`,
      field: 'condition',
      before: `− ${conditionText(condition)}`,
      after: '—',
      sign: 'removed',
    })),
    ...card.conditions_added.map((condition, index) => ({
      key: `added-${index}-${conditionText(condition)}`,
      field: 'condition',
      before: '—',
      after: `+ ${conditionText(condition)}`,
      sign: 'added',
    })),
  ]
}

function ChangeRows({ card, changeRequest, operation, validation, labels }) {
  const lines = changeLines(card, labels)
  const rows = lines.length > 0 ? lines : [{ key: 'none' }]
  const link = reviewBlockLink({ card, operation, changeRequest })
  const { errors, warnings } = findingsForOperation({
    validation,
    sequence: card.sequence,
  })
  const span = rows.length

  return (
    <tbody
      className="lox-cr-review__change"
      data-testid={`cr-change-row-${card.sequence}`}
    >
      {rows.map((line, index) => (
        <tr key={line.key}>
          {index === 0 && (
            <>
              <td rowSpan={span} className="lox-cr-review__seq">
                {card.sequence}
              </td>
              <td rowSpan={span}>
                {link && (
                  <Link
                    className="button is-primary is-small lox-view-button"
                    to={link}
                    aria-label={`View change ${card.sequence}`}
                  >
                    <IconWithHelperText
                      icon={faBinoculars}
                      helperText="View block"
                    />
                  </Link>
                )}
              </td>
              <td rowSpan={span} className="lox-cr-review__change-title">
                {diffCardTitle(card)}
                {card.name && (
                  <span className="lox-cr-review__change-name">
                    {card.name}
                  </span>
                )}
              </td>
            </>
          )}
          <td className="lox-cr-review__field">{line.field ?? '—'}</td>
          <td
            className={`lox-cr-review__before ${
              line.sign === 'removed' ? 'is-removed' : ''
            }`}
          >
            {line.before ?? '—'}
          </td>
          <td
            className={`lox-cr-review__after ${
              line.sign === 'added' ? 'is-added' : ''
            }`}
          >
            {line.after ?? '—'}
          </td>
          {index === 0 && (
            <td rowSpan={span} className="has-text-centered">
              <FindingsBadge findings={[...errors, ...warnings]} />
            </td>
          )}
        </tr>
      ))}
    </tbody>
  )
}

export default function ChangeRequestChangesTable({
  groups,
  changeRequest,
  operations,
  validation,
  actionLabels,
  treatmentNamesById,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set())
  if (groups.length === 0) {
    return <p className="is-size-7 has-text-grey">No changes yet.</p>
  }
  const labels = { actionLabels, treatmentNamesById }
  const operationFor = (sequence) =>
    operations.find((operation) => operation.sequence === sequence) ?? null
  const groupKey = (group) => group.treatmentId ?? 'unknown'
  const toggle = (key) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="table-container mb-0 lox-cr-review__table-wrap">
      <table
        className="table is-fullwidth lox-cr-review__table"
        data-testid="cr-changes-table"
      >
        <thead>
          <tr>
            <th>#</th>
            <th aria-label="View" />
            <th>Change</th>
            <th>Field</th>
            <th>Before</th>
            <th>After</th>
            <th aria-label="Findings" />
          </tr>
        </thead>
        {groups.map((group) => {
          const key = groupKey(group)
          const isOpen = !collapsed.has(key)
          return (
            <React.Fragment key={key}>
              <tbody>
                <tr className="lox-cr-review__group" data-testid="cr-group-row">
                  <th colSpan={COLUMN_COUNT}>
                    <button
                      type="button"
                      className="lox-cr-review__group-toggle"
                      aria-expanded={isOpen}
                      onClick={() => toggle(key)}
                    >
                      <FontAwesomeIcon
                        icon={isOpen ? faChevronDown : faChevronRight}
                        className="lox-cr-review__group-chevron"
                      />
                      {group.label}
                      <span className="lox-cr-review__count">
                        {plural(group.cards.length, 'change')}
                      </span>
                    </button>
                  </th>
                </tr>
              </tbody>
              {isOpen &&
                group.cards.map((card) => (
                  <ChangeRows
                    key={card.sequence}
                    card={card}
                    changeRequest={changeRequest}
                    operation={operationFor(card.sequence)}
                    validation={validation}
                    labels={labels}
                  />
                ))}
            </React.Fragment>
          )
        })}
      </table>
    </div>
  )
}
